/**
 * Analytics Consent — the pure, client-safe core (ADR-075). A visitor's recorded
 * choice about whether Matomo may run, held in the ADR-005 `en_consent`
 * client-preference cookie. This module owns the value shape (parse/serialize)
 * and the single predicate that decides whether analytics are allowed for a
 * given `CONSENT_MODE`; the cookie I/O lives server-side in `consent.server.ts`.
 *
 * State machine (recorded choice + when it was made):
 * - `unset`     — no cookie yet; a fresh visitor. Gated → no tracking.
 * - `accepted`  — opted in. Analytics run in every mode.
 * - `declined`  — opted out. Analytics never run.
 * - `dismissed` — acknowledged the cookieless notice without opting out
 *                 (EPT-144); treated as "not declined" so cookieless keeps
 *                 counting, but gated still withholds until an explicit accept.
 */

/** A recorded consent decision (an actual choice was persisted). */
export type ConsentDecision = 'accepted' | 'declined' | 'dismissed'

/** The resolved consent state, including the pre-choice `unset`. */
export type ConsentState = 'unset' | ConsentDecision

/** The deploy-time consent posture (`CONSENT_MODE`). */
export type ConsentMode = 'gated' | 'cookieless'

/** A resolved Analytics Consent: the state and when it was recorded (ms epoch). */
export type Consent = {
	state: ConsentState
	/** When the decision was recorded; `null` while `unset`. */
	at: number | null
}

/** The resolved value for a visitor who has made no choice yet. */
export const UNSET_CONSENT: Consent = { state: 'unset', at: null }

const DECISIONS: ReadonlySet<string> = new Set([
	'accepted',
	'declined',
	'dismissed',
])

/** Serialize a recorded decision to the `en_consent` cookie value (`state.at`). */
export function serializeConsent(decision: ConsentDecision, at: number): string {
	return `${decision}.${at}`
}

/**
 * Parse the `en_consent` cookie value back to a {@link Consent}. Anything
 * missing or malformed resolves to {@link UNSET_CONSENT} — a corrupt cookie must
 * never be read as consent to track.
 */
export function parseConsent(raw: string | undefined | null): Consent {
	if (!raw) return UNSET_CONSENT
	const [state, atRaw] = raw.split('.')
	if (!state || !DECISIONS.has(state)) return UNSET_CONSENT
	if (!atRaw) return UNSET_CONSENT
	const at = Number(atRaw)
	if (!Number.isFinite(at)) return UNSET_CONSENT
	return { state: state as ConsentDecision, at }
}

/**
 * The single gate both the client mount and the server tracker obey: may
 * analytics run for this consent state under this mode? Gated requires an
 * explicit opt-in; cookieless runs unless the visitor opted out (EPT-144).
 */
export function analyticsAllowed(state: ConsentState, mode: ConsentMode): boolean {
	if (mode === 'cookieless') return state !== 'declined'
	return state === 'accepted'
}
